import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import styles from './DesignPage.module.css';

const PORTFOLIO_ITEMS = [
  {
    src: '/marilyn_brand_package.jpeg',
    alt: 'Marilyn Johnson brand package: business cards, bookmarks, and print materials',
    tag: 'Print Design',
    label: 'Marilyn Johnson / Brand Package',
  },
  {
    src: '/eddie-detail - Edited.jpg',
    alt: 'Eddie detail work: cards and print materials',
    tag: 'Print Design',
    label: 'Eddie / Detail Cards',
  },
];

const BOOKING_URL = 'https://calendar.app.google/4YtwnKduScqcFVjc9';

const MARQUEE_ITEMS = [
  'Business Cards', 'Web Design', 'Brand Identity',
  'Bookmarks', 'Logo Design', 'Print Materials', 'Phoenix, AZ',
  'Business Cards', 'Web Design', 'Brand Identity',
  'Bookmarks', 'Logo Design', 'Print Materials', 'Phoenix, AZ',
];

const SECTION_FIGURES = {
  services: { fig: 'Fig. 01', label: 'What I Offer' },
  portfolio: { fig: 'Plate 02', label: 'Recent Work' },
  testimonial: { fig: 'Fig. 03', label: 'Trusted Locals' },
  process: { fig: 'Fig. 04', label: 'How It Works' },
  whyMe: { fig: 'Fig. 05', label: 'Why Work With Me' },
  packages: { fig: 'Fig. 06', label: 'Packages' },
};

const SERVICES = [
  {
    number: '01',
    title: 'Print Design',
    description:
      'Business cards people keep. Bookmarks, flyers, and materials with finishes that feel intentional, because they are.',
    items: ['Business Cards', 'Bookmarks', 'Flyers & Brochures', 'Print Coordination & Delivery'],
  },
  {
    number: '02',
    title: 'Web Design',
    description:
      'Clean, fast websites built to convert visitors into clients. Mobile-first, zero templates. Every pixel is purposeful.',
    items: ['Landing Pages', 'Portfolio Sites', 'Small Business Sites', 'Mobile Responsive'],
  },
  {
    number: '03',
    title: 'Brand Identity',
    description:
      'Logo, palette, typography. The full visual language of your brand. Every source file is yours. Forever.',
    items: ['Logo Design', 'Color System', 'Typography', 'Source Files Included'],
  },
];

const PROCESS = [
  {
    step: '01',
    title: 'Discovery',
    description: 'We talk about your brand, your audience, and what you want people to feel when they see it.',
  },
  {
    step: '02',
    title: 'Design',
    description: 'I build concepts from scratch, tailored to you. No templates, no shortcuts.',
  },
  {
    step: '03',
    title: 'Refine',
    description: 'You give feedback. We go back and forth until every detail is exactly right.',
  },
  {
    step: '04',
    title: 'Deliver',
    description: 'Print-ready, web-ready files. Every format, every source file is yours to keep.',
  },
];

const PACKAGES = [
  {
    title: 'Print & Cards',
    items: [
      'Business cards, bookmarks, flyers',
      'Custom artwork. No templates',
      'Print-ready files',
      'Print coordination available',
    ],
    cta: 'Get Started',
    featured: true,
  },
  {
    title: 'Logo Design',
    items: [
      'Custom logo, built from scratch',
      '2 concepts, unlimited refinements',
      'Full source files (AI, SVG, PNG)',
      'Print & web-ready formats',
    ],
    cta: 'Get Started',
    featured: false,
  },
  {
    title: 'Brand Identity',
    items: [
      'Logo design',
      'Color + typography system',
      'Brand guidelines',
      'All source files',
    ],
    cta: 'Get Started',
    featured: false,
  },
  {
    title: 'Web Design',
    items: [
      'Custom design',
      'Mobile responsive',
      'Up to 5 pages',
      'Launch support',
    ],
    cta: "Let's Talk",
    featured: false,
  },
];

const WHY_POINTS = [
  {
    title: 'Custom, not templated',
    desc: 'Every design is built from scratch to reflect your brand specifically.',
  },
  {
    title: 'You own every file',
    desc: 'Source files, print files, web assets. All yours at delivery. No strings.',
  },
  {
    title: 'Design → Print → Delivery',
    desc: "Full-service coordination. You don't have to manage three different vendors.",
  },
  {
    title: 'Premium finishes',
    desc: 'Embossed, soft-touch, foil. The details people notice and remember.',
  },
];

const PROCESS_INTERVAL_MS = 4500;

const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11 } },
};

const LOCALS_INTRO =
  'Three businesses I keep coming back to. Different trades, same standard.';

const LOCALS = [
  {
    logo: '/saguaro-pallet-logo-transparent.png',
    alt: 'Saguaro Pallets logo',
    name: 'Saguaro Pallets',
    url: 'https://www.saguaropallets.com/',
    urlLabel: 'saguaropallets.com',
  },
  {
    logo: '/guero-logo.png',
    alt: "Guero's Barbershop logo",
    name: "Guero's Barbershop",
    url: 'https://www.guerosbarbershop.com/',
    urlLabel: 'guerosbarbershop.com',
  },
  {
    logo: '/dustezelogotrans.png',
    alt: 'Dust Eze logo',
    name: 'Dust Eze',
    url: 'https://www.dustezehvac.com/',
    urlLabel: 'dustezehvac.com',
  },
];

const DesignPage = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const prevSlide = () => setSlideIndex((i) => (i === 0 ? PORTFOLIO_ITEMS.length - 1 : i - 1));
  const nextSlide = () => setSlideIndex((i) => (i === PORTFOLIO_ITEMS.length - 1 ? 0 : i + 1));
  const currentSlide = PORTFOLIO_ITEMS[slideIndex];

  // Process auto-advance: restarts on manual tab click
  const [activeStep, setActiveStep] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveStep((i) => (i + 1) % PROCESS.length);
    }, PROCESS_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [activeStep]);

  const currentStep = PROCESS[activeStep];

  return (
  <>
    <Helmet>
      <title>Design Services / Cards, Print & Web | Daniel Ray Rangel</title>
      <meta
        name="description"
        content="Premium business card, print, and web design for artists and small businesses in Phoenix, AZ. Custom work with embossed finishes, brand identity packages, and full websites."
      />
      <link rel="canonical" href="https://www.danielrayrangel.com/brand" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.danielrayrangel.com/brand" />
      <meta property="og:title" content="Design Services / Cards, Print & Web | Daniel Ray Rangel" />
      <meta property="og:description" content="Premium business card, print, and web design for artists and small businesses in Phoenix, AZ. Custom work with embossed finishes, brand identity packages, and full websites." />
      <meta property="og:image" content="https://www.danielrayrangel.com/marilyn_brand_package.jpeg" />
      <meta property="og:site_name" content="Exhale Software Solutions" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Design Services / Cards, Print & Web | Daniel Ray Rangel" />
      <meta name="twitter:description" content="Premium business card, print, and web design for artists and small businesses in Phoenix, AZ." />
      <meta name="twitter:image" content="https://www.danielrayrangel.com/marilyn_brand_package.jpeg" />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,300..900,0..100,0..1;1,9..144,300..900,0..100,0..1&family=JetBrains+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />
    </Helmet>

    <main className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>

        <div className={styles.marqueeWrapper} aria-hidden="true">
          <div className={styles.marqueeTrack}>
            {MARQUEE_ITEMS.map((item, i) => (
              <span key={i} className={styles.marqueeItem}>
                {item}<span className={styles.marqueeDot}> ✦ </span>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.heroCredit} aria-hidden="true">
          <span>Vol. 01 · Phoenix · est. 2024</span>
          <span className={styles.heroCreditRight}>No. 001 / brand &amp; print</span>
        </div>

        <motion.div
          className={styles.bookingSticker}
          initial={{ opacity: 0, y: -10, rotate: -4 }}
          animate={{ opacity: 1, y: 0, rotate: -4 }}
          transition={{ delay: 1.0, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          aria-hidden="true"
        >
          <span className={styles.bookingDot} />
          <div className={styles.bookingCopy}>
            <span className={styles.bookingLabel}>Now Booking</span>
            <span className={styles.bookingSub}>Phoenix · Remote</span>
          </div>
        </motion.div>

        <motion.div
          className={styles.heroContent}
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.p className={styles.heroLabel} variants={fadeUp}>
            Print <span className={styles.labelStar}>✦</span> Web <span className={styles.labelStar}>✦</span> Brand Identity
          </motion.p>

          <motion.h1 className={styles.heroTitle} variants={fadeUp}>
            Built for brands<br />
            <em>that mean it.</em>
          </motion.h1>

          <motion.p className={styles.heroSub} variants={fadeUp}>
            Premium design for artists and small businesses in Phoenix.
            Business cards, print materials, and websites crafted to make people stop.
          </motion.p>

          <motion.div className={styles.heroActions} variants={fadeUp}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaPrimary}
            >
              Book a Free Call
            </a>
            <a href="#work" className={styles.ctaSecondary}>
              See Recent Work
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className={styles.scrollIndicator}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          aria-hidden="true"
        >
          <div className={styles.scrollLine} />
          <span>Scroll</span>
        </motion.div>

      </section>

      {/* ── SERVICES ── */}
      <section className={styles.services}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <p className={styles.sectionLabel}>
              <span className={styles.figMark}>{SECTION_FIGURES.services.fig}</span>
              <span className={styles.figDash}>/</span>
              {SECTION_FIGURES.services.label}
            </p>
            <h2 className={styles.sectionTitle}>
              Three ways I can<br />help <em className={styles.titleEm}>your brand.</em>
            </h2>
          </motion.div>

          <motion.div
            className={styles.servicesGrid}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            {SERVICES.map((service, i) => (
              <motion.div key={service.number} className={styles.serviceCard} variants={fadeUp}>
                <span className={`${styles.serviceTag} ${i % 2 === 0 ? styles.tiltL : styles.tiltR}`}>
                  No. {service.number}
                </span>
                <span className={styles.serviceNumber}>{service.number}</span>
                <h3 className={styles.serviceTitle}>{service.title}</h3>
                <p className={styles.serviceDescription}>{service.description}</p>
                <ul className={styles.serviceList}>
                  {service.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── RECENT WORK ── */}
      <section className={styles.portfolio} id="work">
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <p className={styles.sectionLabel}>
              <span className={styles.figMark}>{SECTION_FIGURES.portfolio.fig}</span>
              <span className={styles.figDash}>/</span>
              {SECTION_FIGURES.portfolio.label}
            </p>
            <h2 className={styles.sectionTitle}>
              Designed with intention.<br /><em className={styles.titleEm}>Printed to impress.</em>
            </h2>
          </motion.div>

          <motion.div
            className={styles.portfolioSingle}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <div className={styles.portfolioShowcase}>
              <img
                src={currentSlide.src}
                alt={currentSlide.alt}
                className={styles.portfolioShowcaseImg}
              />
              <button className={styles.arrowLeft} onClick={prevSlide} aria-label="Previous image">&#8592;</button>
              <button className={styles.arrowRight} onClick={nextSlide} aria-label="Next image">&#8594;</button>
            </div>
            <div className={styles.portfolioCaption}>
              <span className={styles.captionFig}>
                Plate {String(slideIndex + 1).padStart(2, '0')} / {String(PORTFOLIO_ITEMS.length).padStart(2, '0')}
              </span>
              <span className={styles.captionStar}>✦</span>
              <span className={styles.captionTag}>{currentSlide.tag}</span>
              <span className={styles.captionDash}> / </span>
              <span className={styles.captionLabel}>{currentSlide.label}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className={styles.testimonial}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <p className={styles.sectionLabelLight}>
              <span className={styles.figMarkLight}>{SECTION_FIGURES.testimonial.fig}</span>
              <span className={styles.figDash}>/</span>
              {SECTION_FIGURES.testimonial.label}
            </p>
            <h2 className={styles.sectionTitleLight}>
              I wouldn't choose<br /><em className={styles.titleEmLight}>anyone else.</em>
            </h2>
            <p className={styles.localsIntro}>{LOCALS_INTRO}</p>
          </motion.div>

          <motion.div
            className={styles.localsRow}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            {LOCALS.map((local) => (
              <motion.a
                key={local.url}
                href={local.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.localItem}
                variants={fadeUp}
                aria-label={`Visit ${local.name} at ${local.urlLabel}`}
              >
                <img
                  src={local.logo}
                  alt={local.alt}
                  className={styles.localLogo}
                  loading="lazy"
                />
                <span className={styles.localUrl}>
                  {local.urlLabel}
                  <span className={styles.localArrow} aria-hidden="true">→</span>
                </span>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className={styles.process}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <p className={styles.sectionLabelLight}>
              <span className={styles.figMarkLight}>{SECTION_FIGURES.process.fig}</span>
              <span className={styles.figDash}>/</span>
              {SECTION_FIGURES.process.label}
            </p>
            <h2 className={styles.sectionTitleLight}>
              From idea to <em className={styles.titleEmLight}>in-hand.</em>
            </h2>
          </motion.div>

          {/* Desktop: 4-column timeline */}
          <motion.div
            className={styles.processSteps}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            {PROCESS.map((step) => (
              <motion.div key={step.step} className={styles.processStep} variants={fadeUp}>
                <span className={styles.processNumber}>{step.step}</span>
                <h3 className={styles.processTitle}>{step.title}</h3>
                <p className={styles.processDescription}>{step.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Mobile: auto-advancing focused-step card with progress bar */}
          <motion.div
            className={styles.processMobile}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.processProgress} aria-hidden="true">
              <div
                key={activeStep}
                className={styles.processProgressFill}
                style={{ animationDuration: `${PROCESS_INTERVAL_MS}ms` }}
              />
            </div>

            <div className={styles.processCardMeta}>
              <span className={styles.processCardCount}>
                Step {String(activeStep + 1).padStart(2, '0')} / {String(PROCESS.length).padStart(2, '0')}
              </span>
              <span className={styles.processCardLive}>
                <span className={styles.processLiveDot} aria-hidden="true" />
                auto
              </span>
            </div>

            <div className={styles.processCardBody}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep.step}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className={styles.processCardNumber}>{currentStep.step}</span>
                  <h3 className={styles.processCardTitle}>{currentStep.title}</h3>
                  <p className={styles.processCardDesc}>{currentStep.description}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className={styles.processTabs} role="tablist" aria-label="Process steps">
              {PROCESS.map((step, i) => (
                <button
                  key={step.step}
                  type="button"
                  role="tab"
                  aria-selected={i === activeStep}
                  className={`${styles.processTab} ${i === activeStep ? styles.processTabActive : ''}`}
                  onClick={() => setActiveStep(i)}
                >
                  <span className={styles.processTabNum}>{step.step}</span>
                  <span className={styles.processTabLabel}>{step.title}</span>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── WHY ME ── */}
      <section className={styles.whyMe}>
        <div className={styles.container}>
          <div className={styles.whyMeGrid}>
            <motion.div
              className={styles.whyMeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={fadeUp}
            >
              <p className={styles.sectionLabel}>
                <span className={styles.figMark}>{SECTION_FIGURES.whyMe.fig}</span>
                <span className={styles.figDash}>/</span>
                {SECTION_FIGURES.whyMe.label}
              </p>
              <h2 className={styles.whyMeTitle}>
                One designer.<br /><em>Full ownership.</em>
              </h2>
              <p className={styles.whyMeSubtext}>
                Local to Phoenix and the West Valley. You'll always work directly with me.
                Not a project manager, not an offshore team. Just good design, done right.
              </p>
            </motion.div>

            <motion.div
              className={styles.whyMeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={stagger}
            >
              {WHY_POINTS.map((point, i) => (
                <motion.div key={i} className={styles.whyPoint} variants={fadeUp}>
                  <span className={styles.whyPointNumber}>{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h4 className={styles.whyPointTitle}>{point.title}</h4>
                    <p className={styles.whyPointDesc}>{point.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── PACKAGES ── */}
      <section className={styles.pricing}>
        <div className={styles.container}>
          <motion.div
            className={styles.sectionHeader}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <p className={styles.sectionLabel}>
              <span className={styles.figMark}>{SECTION_FIGURES.packages.fig}</span>
              <span className={styles.figDash}>/</span>
              {SECTION_FIGURES.packages.label}
            </p>
            <h2 className={styles.sectionTitle}>
              Pick your<br /><em className={styles.titleEm}>starting point.</em>
            </h2>
          </motion.div>

          <motion.div
            className={styles.pricingGrid}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={stagger}
          >
            {PACKAGES.map((plan) => (
              <motion.div
                key={plan.title}
                className={`${styles.pricingCard} ${plan.featured ? styles.pricingCardFeatured : ''}`}
                variants={fadeUp}
              >
                {plan.featured && (
                  <span className={styles.featuredBadge}>Most Popular</span>
                )}
                <h3 className={styles.pricingTitle}>{plan.title}</h3>
                <ul className={styles.pricingList}>
                  {plan.items.map((item) => (
                    <li key={item}>
                      <span className={styles.checkmark}>✦</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={plan.featured ? styles.pricingCtaFeatured : styles.pricingCta}
                >
                  {plan.cta}
                </a>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            className={styles.pricingDisclaimer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            Every project starts with a conversation. Not sure where to begin?{' '}
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Just reach out.
            </a>
          </motion.p>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className={styles.finalCta}>
        <motion.div
          className={styles.finalCtaContent}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
        >
          <motion.p className={styles.finalCtaLabel} variants={fadeUp}>
            Phoenix <span className={styles.labelStar}>✦</span> West Valley <span className={styles.labelStar}>✦</span> Remote
          </motion.p>
          <motion.h2 className={styles.finalCtaTitle} variants={fadeUp}>
            Ready to look like<br /><em>you mean it?</em>
          </motion.h2>
          <motion.p className={styles.finalCtaSub} variants={fadeUp}>
            Book a free 30-minute call. We'll talk through your brand,
            what you need, and what's possible.
          </motion.p>
          <motion.div variants={fadeUp}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.finalCtaButton}
            >
              Book a Free Call
            </a>
          </motion.div>
          <motion.p className={styles.colophon} variants={fadeUp}>
            <span>Designed &amp; built by Daniel Rangel</span>
            <span className={styles.colophonStar}>✦</span>
            <span>Phoenix, AZ</span>
            <span className={styles.colophonStar}>✦</span>
            <span>danielrayrangel.com</span>
          </motion.p>
        </motion.div>
      </section>

    </main>
  </>
  );
};

export default DesignPage;
